import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  Button,
  Card,
  Cluster,
  EmptyState,
  Grid,
  HintMark,
  Input,
  LabelWithHint,
  Select,
  Spinner,
  Stack,
  Tooltip,
  useToast,
} from "@salesapp/ui";
import { reportSaveFailure, reportSaved } from "../../lib/saveNotice";
import {
  opsApi,
  type ForecastPerson,
  type KpiItem,
  type SalesPlan,
  type SalesPlanMilestone,
  type SalesPlanPeriod,
  type Territory,
} from "../../lib/opsApi";
import { useLocale } from "../../shell/LocaleProvider";
import { RecordAuditInfo } from "./RecordAuditInfo";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const MEASUREMENTS: Array<[string, string]> = [
  ["activity_email", "Emails"],
  ["activity_call", "Cold calls"],
  ["activity_meeting", "Meetings"],
  ["activity_demo", "Demos"],
  ["activity_conversation", "Conversations"],
  ["quote_sent", "Proposals sent or accepted"],
  ["opportunity_created", "Opportunities created"],
  ["opportunity_won", "Wins"],
  ["lead_created", "Leads created"],
  ["manual", "Not counted from CRM records"],
];

type CountDraft = Record<string, string>;
type CustomDraft = { id: string; title: string; description: string; hours: string };

function countLeft(name: string, target: number, actual: number, label: string): string {
  if (target <= 0) return "";
  if (actual >= target) return `${label} met`;
  const left = target - actual;
  const shown = Number.isInteger(left) ? String(left) : left.toFixed(2);
  return `${shown} ${name} still to log for ${label}`;
}

function monthValue(choice: string): number | null {
  return choice === "fy" ? null : Number(choice);
}

function Field({ label, tooltip, children }: { label: string; tooltip: string; children: ReactNode }) {
  return (
    <Stack gap={1}>
      <LabelWithHint label={label} tooltip={tooltip} />
      {children}
    </Stack>
  );
}

export function SalesPlanPanel({
  orgId,
  year,
  people,
  selectedUser,
  onSelectUser,
  canEdit,
  territoryId,
  territories,
  sessionUserId,
}: {
  orgId: string;
  year: number;
  people: ForecastPerson[];
  selectedUser: string;
  onSelectUser: (userId: string) => void;
  canEdit: boolean;
  territoryId: string;
  territories: Territory[];
  sessionUserId?: string;
}) {
  const { show } = useToast();
  const { fiscalYearStartMonth, orgCurrency } = useLocale();
  const choices = canEdit ? people : people.filter((person) => person.user_id === sessionUserId);
  const [plan, setPlan] = useState<SalesPlan | null>(null);
  const [catalogue, setCatalogue] = useState<KpiItem[]>([]);
  const [cadence, setCadence] = useState("month");
  const [narrative, setNarrative] = useState("");
  const [planOwners, setPlanOwners] = useState<Array<{ user_id: string; display_name: string }>>([]);
  const [startChoice, setStartChoice] = useState("fy");
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  const [sales, setSales] = useState<Record<string, string>>({});
  const [counts, setCounts] = useState<Record<string, CountDraft>>({});
  const [customs, setCustoms] = useState<Record<string, CustomDraft[]>>({});
  const [customTitle, setCustomTitle] = useState<Record<string, string>>({});
  const [customNote, setCustomNote] = useState<Record<string, string>>({});
  const [milestones, setMilestones] = useState<SalesPlanMilestone[]>([]);
  const [newItem, setNewItem] = useState("");
  const [newMeasurement, setNewMeasurement] = useState("activity_call");
  const [copyFrom, setCopyFrom] = useState("");
  const [copyTo, setCopyTo] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mayChange, setMayChange] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingPlan, setEditingPlan] = useState(false);
  const customSeq = useRef(0);

  const apply = useCallback((
    next: SalesPlan | null,
    items: KpiItem[],
    owners?: Array<{ user_id: string; display_name: string }>,
  ) => {
    setPlan(next);
    setCatalogue(items);
    if (owners) setPlanOwners(owners);
    if (!next) {
      setStartChoice("fy");
      setPicked({});
      setCustoms({});
      return;
    }
    setCadence(next.cadence);
    setNarrative(next.narrative || "");
    setStartChoice(next.start_month ? String(next.start_month) : "fy");
    const nextSales: Record<string, string> = {};
    const nextCounts: Record<string, CountDraft> = {};
    const nextPicked: Record<string, string[]> = {};
    const nextCustoms: Record<string, CustomDraft[]> = {};
    for (const period of next.periods) {
      nextSales[period.period_start] = period.sales_target;
      const lineCounts: CountDraft = {};
      for (const line of period.lines) lineCounts[line.item_id] = line.target_count;
      nextCounts[period.period_start] = lineCounts;
      nextPicked[period.period_start] = period.lines.map((line) => line.item_id);
      nextCustoms[period.period_start] = (period.customs ?? []).map((custom) => ({
        id: custom.id,
        title: custom.title,
        description: custom.description,
        hours: custom.hours,
      }));
    }
    setSales(nextSales);
    setCounts(nextCounts);
    setPicked(nextPicked);
    setCustoms(nextCustoms);
    setMilestones(next.milestones);
  }, []);

  const selectedChoice = choices.find((person) => person.user_id === selectedUser) ?? null;
  const activeUser = selectedChoice?.user_id ?? "";
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    if (!activeUser) {
      setPlan(null);
      setPlanOwners([]);
      setMayChange(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const body = await opsApi.getSalesPlan(orgId, activeUser, year);
      if (seq !== loadSeq.current) return;
      setMayChange(Boolean(body.can_edit));
      apply(body.plan, body.catalogue, body.plan_owners);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setMayChange(false);
      setError(err instanceof Error ? err.message : "Sales plan could not be loaded.");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [activeUser, apply, orgId, year]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setCopyFrom("");
    setCopyTo([]);
    setError(null);
    setEditingPlan(false);
  }, [territoryId, activeUser]);

  const visiblePlan = activeUser ? plan : null;
  const savedTerritoryId = visiblePlan?.territory_id || (territoryId === "all" ? "" : territoryId);
  const matchedTerritory = savedTerritoryId
    ? territories.find((item) => item.id === savedTerritoryId)
    : territories.find((item) => item.code === "global");
  const fiscalMonth = matchedTerritory?.fiscal_year_start_month || fiscalYearStartMonth || 1;
  const fiscalName = MONTHS[fiscalMonth - 1] ?? "January";
  const territoryCurrency = (matchedTerritory?.currency || orgCurrency || "USD").toUpperCase();
  const territoryName = matchedTerritory?.name || "the organisation";
  const shownCurrency = visiblePlan && visiblePlan.status !== "draft"
    ? (visiblePlan.currency || territoryCurrency)
    : territoryCurrency;
  const personName = selectedChoice?.display_name || visiblePlan?.display_name || "Sales person";
  const others = choices.filter((person) => person.user_id !== activeUser);
  const copySources = planOwners.filter((owner) => (
    owner.user_id !== activeUser && choices.some((person) => person.user_id === owner.user_id)
  ));
  // The header hides the territory picker when Global is the only territory, and
  // the address stays on "all". Create still needs Global's id, or the button
  // stays disabled with nothing to select.
  const globalTerritory = territories.find((item) => item.code === "global") ?? null;
  const mustChooseTerritory = territories.some((item) => item.code !== "global");
  const territory = territoryId === "all"
    ? (mustChooseTerritory ? null : globalTerritory?.id ?? null)
    : territoryId;
  const draft = visiblePlan?.status === "draft";
  const editable = mayChange;

  function payload(structural?: { cadence?: string; start?: string }) {
    const choice = structural?.start ?? startChoice;
    return {
      fiscal_year: year,
      cadence: structural?.cadence ?? plan?.cadence,
      start_month: monthValue(choice),
      narrative,
      periods: structural
        ? undefined
        : (plan?.periods ?? []).map((period) => ({
            period_start: period.period_start,
            period_end: period.period_end,
            sales_target: sales[period.period_start] || "0",
            item_ids: picked[period.period_start] ?? [],
            lines: (picked[period.period_start] ?? []).map((itemId) => ({
              item_id: itemId,
              target_count: counts[period.period_start]?.[itemId] || "0",
            })),
            customs: (customs[period.period_start] ?? []).map((custom) => ({
              id: custom.id.startsWith("new-") ? null : custom.id,
              title: custom.title,
              description: custom.description,
              hours: custom.hours || "0",
            })),
          })),
      milestones: milestones.map((item) => ({
        id: item.id.startsWith("new-") ? null : item.id,
        title: item.title,
        notes: item.notes,
        confirmed: item.confirmed,
      })),
    };
  }

  async function startPlan() {
    if (!selectedUser || !territory) {
      setError("Select a territory first.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = await opsApi.createSalesPlan(orgId, selectedUser, {
        fiscal_year: year,
        cadence,
        currency: shownCurrency,
        territory_id: territory,
        start_month: monthValue(startChoice),
      });
      apply(body.plan, body.catalogue, body.plan_owners);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sales plan could not be started.");
    } finally {
      setBusy(false);
    }
  }

  async function save(structural?: { cadence?: string; start?: string }) {
    if (!selectedUser || !plan) return;
    setBusy(true);
    setError(null);
    try {
      const body = await opsApi.updateSalesPlan(orgId, selectedUser, payload(structural));
      apply(body.plan, body.catalogue, body.plan_owners);
      reportSaved(show);
    } catch (err) {
      setError(reportSaveFailure(show, err, "Sales plan could not be saved."));
      if (structural) await load();
    } finally {
      setBusy(false);
    }
  }

  async function downloadWorking() {
    if (!selectedUser) return;
    setError(null);
    try {
      await opsApi.downloadSalesPlan(orgId, selectedUser, year);
    } catch (err) {
      setError(err instanceof Error ? err.message : "PDF could not be downloaded.");
    }
  }

  async function issue() {
    if (!selectedUser) return;
    setBusy(true);
    setError(null);
    try {
      const issued = await opsApi.issueSalesPlan(orgId, selectedUser, year);
      await opsApi.downloadSalesPlan(orgId, selectedUser, year, issued.issue_id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Issued PDF could not be created.");
    } finally {
      setBusy(false);
    }
  }

  async function addItem() {
    const name = newItem.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      const created = await opsApi.createKpiItem(orgId, {
        name,
        measurement: newMeasurement,
        included_by_default: false,
      });
      setNewItem("");
      setCatalogue((current) => [...current, created]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "KPI item could not be added.");
    } finally {
      setBusy(false);
    }
  }

  async function retireItem(itemId: string) {
    setBusy(true);
    setError(null);
    try {
      const updated = await opsApi.updateKpiItem(orgId, itemId, { retired: true });
      setCatalogue((current) => current.map((item) => (item.id === itemId ? updated : item)));
      setPicked((current) => {
        const next: Record<string, string[]> = {};
        for (const [key, ids] of Object.entries(current)) next[key] = ids.filter((id) => id !== itemId);
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "KPI item could not be retired.");
    } finally {
      setBusy(false);
    }
  }

  async function copyFromPerson() {
    if (!selectedUser || !copyFrom) return;
    setBusy(true);
    setError(null);
    try {
      const body = await opsApi.copySalesPlan(orgId, selectedUser, {
        fiscal_year: year,
        source_user_id: copyFrom,
        territory_id: territory,
      });
      apply(body.plan, body.catalogue, body.plan_owners);
      setCopyFrom("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Plan could not be copied.");
    } finally {
      setBusy(false);
    }
  }

  async function applyToSelected() {
    if (!selectedUser || !plan || copyTo.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await opsApi.updateSalesPlan(orgId, selectedUser, payload());
      for (const targetId of copyTo) {
        await opsApi.copySalesPlan(orgId, targetId, {
          fiscal_year: year,
          source_user_id: selectedUser,
          territory_id: territory,
        });
      }
      setCopyTo([]);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Plan could not be applied.");
    } finally {
      setBusy(false);
    }
  }

  function toggleItem(period: SalesPlanPeriod, itemId: string, on: boolean) {
    const key = period.period_start;
    const current = picked[key] ?? [];
    setPicked({
      ...picked,
      [key]: on ? [...current, itemId] : current.filter((id) => id !== itemId),
    });
  }

  function addCustom(period: SalesPlanPeriod) {
    const key = period.period_start;
    const title = (customTitle[key] || "").trim();
    if (!title) return;
    const draft: CustomDraft = {
      id: `new-${key}-${customSeq.current++}`,
      title,
      description: (customNote[key] || "").trim(),
      hours: "0",
    };
    setCustoms({ ...customs, [key]: [...(customs[key] ?? []), draft] });
    setCustomTitle({ ...customTitle, [key]: "" });
    setCustomNote({ ...customNote, [key]: "" });
  }

  const mustPickTerritory = mustChooseTerritory && (!territoryId || territoryId === "all");
  if (mustPickTerritory) {
    return (
      <Stack gap={4} className="sales-plan-panel">
        <Card>
          <p className="home-subtitle">
            Select a territory first. The salesperson list then shows the people assigned to that territory,
            and you can create a new plan or copy an existing one.
          </p>
        </Card>
      </Stack>
    );
  }

  if (choices.length === 0) {
    return (
      <EmptyState
        title={canEdit ? "No one is assigned to this territory" : "No sales plan for you on this territory"}
        description={canEdit
          ? "Assign people to this territory in Organization settings. They appear here after that."
          : "The header territory stays selected. A plan appears here when you are in that territory."}
      />
    );
  }

  const applyCard = visiblePlan && editable ? (
    <Card>
      <Field
        label="Apply this plan to"
        tooltip="Saves this plan, then applies it to each selected person for this year. Use this after planning a role so the same months, KPIs, and hours land on several people. Membership in a territory is not changed."
      >
        <Stack gap={2}>
          {others.length === 0 && (
            <p className="home-subtitle">No other person is on this territory.</p>
          )}
          {others.map((person) => (
            <Cluster key={person.user_id} gap={2} align="center">
              <label>
                <input
                  type="checkbox"
                  aria-label={`Apply plan to ${person.display_name}`}
                  checked={copyTo.includes(person.user_id)}
                  disabled={busy || !draft}
                  onChange={(event) => setCopyTo(event.target.checked
                    ? [...copyTo, person.user_id]
                    : copyTo.filter((id) => id !== person.user_id))}
                />
                {" "}{person.display_name}
              </label>
              <HintMark
                text={`Apply ${personName}'s ${year} plan to ${person.display_name}. Their territory is not changed, and an agreed plan of theirs is left alone.`}
                label={`About applying to ${person.display_name}`}
              />
            </Cluster>
          ))}
          <Tooltip
            text="Saves the open plan, then copies it onto every ticked person."
            label="About apply"
          >
            <Button
              type="button"
              variant="secondary"
              disabled={busy || !draft || copyTo.length === 0}
              onClick={() => void applyToSelected()}
            >
              Apply to selected
            </Button>
          </Tooltip>
        </Stack>
      </Field>
    </Card>
  ) : null;

  const fiscalHint = `Fiscal year (${fiscalName}) is the month set for ${territoryName} in Organization settings. Pick another month when this plan should not start then.`;
  const currencyHint = `Sales targets use ${shownCurrency}, the currency set for ${territoryName} in Organization settings.`;

  return (
    <Stack gap={4} className="sales-plan-panel">
      {error && <p className="crm-error">{error}</p>}
      <Cluster gap={3} align="center">
        <p className="sales-plan-choice">Select a salesperson</p>
        <div className="sales-plan-choice-control">
          <Select
            aria-label="Sales person"
            value={activeUser}
            onChange={(event) => onSelectUser(event.target.value)}
          >
            <option value="">Select a salesperson</option>
            {choices.map((person) => (
              <option key={person.user_id} value={person.user_id}>{person.display_name}</option>
            ))}
          </Select>
        </div>
        {editable && activeUser && (
          <>
            <p className="sales-plan-choice">or copy from an existing sales plan</p>
            <div className="sales-plan-choice-control">
              <Select
                aria-label="Copy from"
                value={copyFrom}
                disabled={busy || (visiblePlan != null && !draft) || copySources.length === 0}
                onChange={(event) => setCopyFrom(event.target.value)}
              >
                <option value="">{copySources.length === 0 ? "No saved plans" : "Choose a person"}</option>
                {copySources.map((person) => (
                  <option key={person.user_id} value={person.user_id}>{person.display_name}</option>
                ))}
              </Select>
            </div>
            <Tooltip
              text="Copies that person's saved plan onto the person selected here. Only people who already have a plan for this year are listed. Territory membership is not changed, and an agreed plan is not replaced."
              label="About copy from"
            >
              <Button
                type="button"
                disabled={busy || !copyFrom || (visiblePlan != null && !draft)}
                onClick={() => void copyFromPerson()}
              >
                Copy onto this plan
              </Button>
            </Tooltip>
            {!visiblePlan && (
              <Tooltip
                text="Creates a new draft plan for the selected person, using the cadence and start month below."
                label="About create new"
              >
                <Button type="button" onClick={() => void startPlan()} disabled={busy || !selectedUser || !territory}>
                  Create new
                </Button>
              </Tooltip>
            )}
          </>
        )}
        {visiblePlan && (
          <Cluster gap={2} align="center">
            <span className="home-subtitle">
              {visiblePlan.status} · {visiblePlan.territory_name || "Unassigned"} · {shownCurrency}
            </span>
            <HintMark
              text="Draft months can still be edited. Once the plan is issued or closed, a month that has already ended keeps its numbers."
              label="About plan status"
            />
          </Cluster>
        )}
        {visiblePlan && editable && (
          <Button type="button" variant="secondary" onClick={() => setEditingPlan((open) => !open)}>
            {editingPlan ? "View plan" : "Edit plan"}
          </Button>
        )}
      </Cluster>
      {loading && activeUser && <Spinner />}
      {!activeUser && (
        <p className="home-subtitle">
          Select a salesperson. The list is the people assigned to {territoryName}.
        </p>
      )}
      {!loading && !visiblePlan && activeUser && (
        <Stack gap={4}>
          <Card>
            <Stack gap={3}>
              <p className="home-subtitle">
                People assigned to {territoryName} are listed above. Create new starts one plan for the selected person and year.
                Once it exists, this page opens on View plan: the targets and what has been logged. Edit plan is where the numbers are changed.
                Months start in {fiscalName}, the fiscal year set for {territoryName}, or on a month you choose.
                Targets use {shownCurrency} from that territory. Saving does not change who belongs to a territory.
              </p>
              <Cluster gap={3} align="end">
                <Field
                  label="Cadence"
                  tooltip="Monthly shows twelve months down the page. Quarterly shows four blocks. Yearly shows one block. This can change only while the plan is a draft."
                >
                  <Select aria-label="Cadence" value={cadence} onChange={(event) => setCadence(event.target.value)} disabled={!editable}>
                    <option value="month">Monthly</option>
                    <option value="quarter">Quarterly</option>
                    <option value="year">Yearly</option>
                  </Select>
                </Field>
                <Field
                  label="Starts in"
                  tooltip={fiscalHint}
                >
                  <Select aria-label="Starts in" value={startChoice} onChange={(event) => setStartChoice(event.target.value)} disabled={!editable}>
                    <option value="fy">Fiscal year ({fiscalName})</option>
                    {MONTHS.map((name, index) => (
                      <option key={name} value={String(index + 1)}>{name}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Currency" tooltip={currencyHint}>
                  <Input aria-label="Currency" value={shownCurrency} disabled readOnly />
                </Field>
                {editable && (
                  <Tooltip text="Creates a new draft plan for this person, year, cadence, and start month." label="About create new plan">
                    <Button type="button" onClick={() => void startPlan()} disabled={busy || !selectedUser || !territory}>Create new</Button>
                  </Tooltip>
                )}
              </Cluster>
            </Stack>
          </Card>
        </Stack>
      )}
      {!loading && visiblePlan && plan && !editingPlan && (
        <Stack gap={4}>
          <Cluster gap={2} align="center">
            <p className="home-subtitle">
              This plan stays on {visiblePlan.territory_name || "its saved territory"}. The header territory stays when you change tabs or reload.
              Saving does not change territory membership.
            </p>
            <HintMark
              text="The plan remembers the territory it was created on. Changing the header filter does not move the plan or the person's territory membership."
              label="About territory on the plan"
            />
          </Cluster>
          <p className="home-subtitle">
            Cold calls, emails, meetings, and demos count when {personName} logs them on a company or contact.
            A note does not count. Connected, Conversation, and Discovery count as meaningful conversations when that item is on the plan.
          </p>
          {visiblePlan.periods.length === 0 ? (
            <p className="home-subtitle">This plan has no periods yet. Edit plan to add them.</p>
          ) : visiblePlan.periods.map((period) => (
            <Card key={period.id}>
              <Stack gap={2}>
                <LabelWithHint
                  label={`${period.label} · ${personName}`}
                  tooltip={`Targets and what ${personName} has logged for ${period.label}.`}
                />
                <p className="home-subtitle">
                  Sales target {shownCurrency} {period.sales_target}. Closed won {shownCurrency} {period.sales_actual}.
                  {period.sales_gap ? ` ${period.sales_gap}` : ""}
                </p>
                {period.lines.length === 0 ? (
                  <p className="home-subtitle">No KPIs are ticked for this period.</p>
                ) : period.lines.map((line) => (
                  <p key={line.item_id} className="home-subtitle">
                    {line.name}: {line.actual} logged of {line.target_count}.
                    {line.gap ? ` ${line.gap}` : ""}
                    {line.definition ? ` ${line.definition}.` : ""}
                  </p>
                ))}
                {(period.customs ?? []).map((custom) => (
                  <p key={custom.id} className="home-subtitle">
                    {custom.title}: {custom.hours} hours.{custom.description ? ` ${custom.description}` : ""}
                  </p>
                ))}
              </Stack>
            </Card>
          ))}
          <Tooltip text="Downloads a working-copy PDF of the live plan. It is labelled as a working copy." label="About download PDF">
            <Button type="button" variant="secondary" disabled={busy} onClick={() => void downloadWorking()}>Download PDF</Button>
          </Tooltip>
        </Stack>
      )}
      {!loading && visiblePlan && plan && editingPlan && (
        <Stack gap={4}>
          <Cluster gap={2} align="center">
            <p className="home-subtitle">
              This plan stays on {visiblePlan.territory_name || "its saved territory"}. The header territory stays when you change tabs or reload.
              Saving does not change territory membership.
            </p>
            <HintMark
              text="The plan remembers the territory it was created on. Changing the header filter does not move the plan or the person's territory membership."
              label="About territory on the plan"
            />
          </Cluster>
          <Cluster gap={3} align="end">
            <Field
              label="Cadence"
              tooltip="Monthly lists every month down the page. Quarterly lists four blocks. Yearly lists one. Changing it while the plan is a draft rebuilds the periods and keeps numbers that still fall inside them."
            >
              <Select
                aria-label="Cadence"
                value={visiblePlan.cadence}
                disabled={!editable || !draft || busy}
                onChange={(event) => void save({ cadence: event.target.value })}
              >
                <option value="month">Monthly</option>
                <option value="quarter">Quarterly</option>
                <option value="year">Yearly</option>
              </Select>
            </Field>
            <Field
              label="Starts in"
              tooltip={fiscalHint}
            >
              <Select
                aria-label="Starts in"
                value={startChoice}
                disabled={!editable || !draft || busy}
                onChange={(event) => {
                  setStartChoice(event.target.value);
                  void save({ start: event.target.value });
                }}
              >
                <option value="fy">Fiscal year ({fiscalName})</option>
                {MONTHS.map((name, index) => (
                  <option key={name} value={String(index + 1)}>{name}</option>
                ))}
              </Select>
            </Field>
            <Field label="Currency" tooltip={currencyHint}>
              <Input aria-label="Plan currency" value={shownCurrency} disabled readOnly />
            </Field>
          </Cluster>
          {applyCard}
          <Stack gap={4}>
            {visiblePlan.periods.map((period) => {
              const key = period.period_start;
              const selected = picked[key] ?? [];
              const locked = !editable || period.locked;
              const periodCustoms = customs[key] ?? [];
              const visibleItems = catalogue.filter((item) => !item.retired_at || selected.includes(item.id));
              return (
                <Grid key={period.id} columns={2} gap={4}>
                  <Card>
                    <Stack gap={3}>
                      <LabelWithHint
                        label={`${period.label} · ${personName}`}
                        tooltip={`Sales target and KPI counts for ${personName} in ${period.label}. Only items ticked on the right are listed here.`}
                      />
                      <Field
                        label="Sales target"
                        tooltip={`Closed-won amount for ${period.label}. Forecast uses this figure.`}
                      >
                        <Input
                          type="number"
                          min={0}
                          aria-label={`Sales target ${period.label}`}
                          value={sales[key] ?? period.sales_target}
                          disabled={locked}
                          onChange={(event) => setSales({ ...sales, [key]: event.target.value })}
                        />
                      </Field>
                      {period.sales_gap ? <p className="home-subtitle">{period.sales_gap}</p> : null}
                      {visibleItems.filter((item) => selected.includes(item.id)).map((item) => {
                        const line = period.lines.find((entry) => entry.item_id === item.id);
                        const value = counts[key]?.[item.id] ?? line?.target_count ?? "0";
                        return (
                          <Field
                            key={item.id}
                            label={item.name}
                            tooltip={item.definition || `Target count of ${item.name} for ${period.label}.`}
                          >
                            <Input
                              type="number"
                              min={0}
                              aria-label={`${item.name} ${period.label}`}
                              value={value}
                              disabled={locked}
                              onChange={(event) => setCounts({
                                ...counts,
                                [key]: { ...(counts[key] ?? {}), [item.id]: event.target.value },
                              })}
                            />
                            {line && line.measurement !== "manual" && line.gap ? (
                              <p className="home-subtitle">{line.gap}</p>
                            ) : null}
                            {(!line || line.measurement === "manual") && (
                              <p className="home-subtitle">
                                {line?.measurement === "manual"
                                  ? countLeft(item.name, Number(value) || 0, Number(line.actual) || 0, period.label) || "Not counted from CRM records."
                                  : "Not counted until this plan is saved."}
                              </p>
                            )}
                          </Field>
                        );
                      })}
                      {periodCustoms.map((custom) => (
                        <Field
                          key={custom.id}
                          label={custom.title}
                          tooltip={
                            custom.description
                              ? `${custom.description} Hours are typed for this month and are not counted from CRM records.`
                              : "Custom item for this month. Hours are typed here and are not counted from CRM records."
                          }
                        >
                          <Cluster gap={2} align="center">
                            <Input
                              type="number"
                              min={0}
                              step="0.5"
                              aria-label={`${custom.title} hours ${period.label}`}
                              value={custom.hours}
                              disabled={locked}
                              onChange={(event) => setCustoms({
                                ...customs,
                                [key]: periodCustoms.map((row) => (
                                  row.id === custom.id ? { ...row, hours: event.target.value } : row
                                )),
                              })}
                            />
                            {!locked && (
                              <Tooltip text={`Remove ${custom.title} from ${period.label}.`} label={`About remove ${custom.title}`}>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setCustoms({
                                    ...customs,
                                    [key]: periodCustoms.filter((row) => row.id !== custom.id),
                                  })}
                                >
                                  Remove
                                </Button>
                              </Tooltip>
                            )}
                          </Cluster>
                          <p className="home-subtitle">Hours. Not counted from CRM records.</p>
                        </Field>
                      ))}
                    </Stack>
                  </Card>
                  <Card>
                    <Stack gap={3}>
                      <LabelWithHint
                        label={`Items for ${period.label}`}
                        tooltip={`Tick the KPIs that belong on ${period.label} only. Other months keep their own ticks.`}
                      />
                      {visibleItems.map((item) => (
                        <Cluster key={item.id} gap={2} align="center">
                          <label>
                            <input
                              type="checkbox"
                              aria-label={`${item.name} for ${period.label}`}
                              checked={selected.includes(item.id)}
                              disabled={locked}
                              onChange={(event) => toggleItem(period, item.id, event.target.checked)}
                            />
                            {" "}{item.name}
                          </label>
                          <HintMark
                            text={item.definition || `Add ${item.name} to ${period.label}. Unticking it removes it from this month only.`}
                            label={`About ${item.name}`}
                          />
                        </Cluster>
                      ))}
                      {!locked && (
                        <Stack gap={2}>
                          <Field
                            label="Custom heading"
                            tooltip={`Name a one-off item for ${period.label}. It is not added to other months or to the shared KPI list.`}
                          >
                            <Input
                              aria-label={`Custom heading ${period.label}`}
                              value={customTitle[key] ?? ""}
                              onChange={(event) => setCustomTitle({ ...customTitle, [key]: event.target.value })}
                            />
                          </Field>
                          <Field
                            label="Description"
                            tooltip="What this custom item means. The description is stored with the month and shown as its tooltip."
                          >
                            <textarea
                              className="crm-textarea"
                              aria-label={`Custom description ${period.label}`}
                              rows={2}
                              value={customNote[key] ?? ""}
                              onChange={(event) => setCustomNote({ ...customNote, [key]: event.target.value })}
                            />
                          </Field>
                          <Tooltip
                            text={`Adds the heading to ${period.label}. The hours field appears on the left so you can type them, then save the plan.`}
                            label="About add custom"
                          >
                            <Button type="button" size="sm" variant="secondary" onClick={() => addCustom(period)}>
                              Add to this month
                            </Button>
                          </Tooltip>
                        </Stack>
                      )}
                    </Stack>
                  </Card>
                </Grid>
              );
            })}
          </Stack>
          <Field
            label="Notes"
            tooltip="Narrative agreed with this person for the year. It is printed on the PDF and does not change the sales targets."
          >
            <textarea
              className="crm-textarea"
              aria-label="Plan notes"
              rows={4}
              value={narrative}
              disabled={!editable}
              onChange={(event) => setNarrative(event.target.value)}
            />
          </Field>
          <Stack gap={2}>
            <LabelWithHint
              label="Milestones"
              tooltip="Checkpoints on this plan, such as a 30, 60, or 90 day conversation. Confirming one records who confirmed it."
            />
            {milestones.map((item, index) => (
              <Cluster key={item.id} gap={2} align="center">
                <Input
                  aria-label={`Milestone ${index + 1}`}
                  value={item.title}
                  disabled={!editable}
                  onChange={(event) => setMilestones(milestones.map((row) => (
                    row.id === item.id ? { ...row, title: event.target.value } : row
                  )))}
                />
                <HintMark
                  text="Short name of this checkpoint. It is stored on the plan and printed on the PDF."
                  label={`About milestone ${index + 1}`}
                />
                <Tooltip
                  text={item.confirmed ? "Mark this milestone open again." : "Record that this milestone was confirmed."}
                  label="About confirm milestone"
                >
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={!editable}
                    onClick={() => setMilestones(milestones.map((row) => (
                      row.id === item.id ? { ...row, confirmed: !row.confirmed } : row
                    )))}
                  >
                    {item.confirmed ? "Confirmed" : "Confirm"}
                  </Button>
                </Tooltip>
              </Cluster>
            ))}
            {editable && (
              <Tooltip text="Adds a blank milestone. Type its name, then save the plan." label="About add milestone">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => setMilestones([
                    ...milestones,
                    { id: `new-${milestones.length}`, title: "", notes: "", confirmed: false },
                  ])}
                >
                  Add milestone
                </Button>
              </Tooltip>
            )}
          </Stack>
          <Stack gap={2}>
            <LabelWithHint
              label="Shared KPI items"
              tooltip="Catalogue for the organisation. A new item can be ticked on any month. Retiring hides it from new ticks. Months that already use it keep it until you untick them and save."
            />
            {catalogue.filter((item) => !item.retired_at).map((item) => (
              <Cluster key={item.id} gap={2} align="center">
                <span>{item.name}</span>
                <HintMark text={item.definition || item.name} label={`About ${item.name}`} />
                {editable && (
                  <Tooltip
                    text={`Hide ${item.name} from new plans. Months that already use it keep it until you untick them and save.`}
                    label={`About retire ${item.name}`}
                  >
                    <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => void retireItem(item.id)}>
                      Retire
                    </Button>
                  </Tooltip>
                )}
              </Cluster>
            ))}
            {editable && (
              <Cluster gap={2} align="end">
                <Field
                  label="New shared KPI"
                  tooltip="Adds an item to the organisation catalogue. Tick it on the months that should use it."
                >
                  <Input aria-label="New KPI name" value={newItem} onChange={(event) => setNewItem(event.target.value)} />
                </Field>
                <Field
                  label="Counted as"
                  tooltip="How actuals are read from CRM records. Manual items are typed targets and are not counted from records."
                >
                  <Select aria-label="New KPI measurement" value={newMeasurement} onChange={(event) => setNewMeasurement(event.target.value)}>
                    {MEASUREMENTS.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                </Field>
                <Tooltip text="Adds this name to the shared catalogue so it can be ticked on any month." label="About add item">
                  <Button type="button" size="sm" disabled={busy} onClick={() => void addItem()}>Add item</Button>
                </Tooltip>
              </Cluster>
            )}
          </Stack>
          <RecordAuditInfo record={plan} />
          {plan.changes.length > 0 && (
            <div className="permission-table-wrap">
              <LabelWithHint
                label="Changes"
                tooltip="Who changed which field, and the value before and after. This is the plan's own history."
              />
              <table className="crm-table">
                <thead>
                  <tr>
                    <th><LabelWithHint label="When" tooltip="When this change was saved." /></th>
                    <th><LabelWithHint label="Who" tooltip="The person who saved this change." /></th>
                    <th><LabelWithHint label="Field" tooltip="The part of the plan that changed." /></th>
                    <th><LabelWithHint label="From" tooltip="The value before the change." /></th>
                    <th><LabelWithHint label="To" tooltip="The value after the change." /></th>
                  </tr>
                </thead>
                <tbody>
                  {plan.changes.map((change) => (
                    <tr key={change.id}>
                      <td>{change.created_at}</td>
                      <td>{change.actor_email || change.actor_user_id || "—"}</td>
                      <td>{change.field_name}</td>
                      <td>{change.old_value || "—"}</td>
                      <td>{change.new_value || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Cluster gap={2}>
            {editable && (
              <Tooltip text="Saves sales targets, the KPIs ticked on each month, custom hours, notes, and milestones." label="About save">
                <Button type="button" disabled={busy} onClick={() => void save()}>Save</Button>
              </Tooltip>
            )}
            <Tooltip text="Downloads a working-copy PDF of the live plan. It is labelled as a working copy." label="About download PDF">
              <Button type="button" variant="secondary" disabled={busy} onClick={() => void downloadWorking()}>Download PDF</Button>
            </Tooltip>
            {editable && plan.status !== "draft" && (
              <Tooltip text="Freezes a PDF snapshot of this plan." label="About issue PDF">
                <Button type="button" variant="secondary" disabled={busy} onClick={() => void issue()}>Issue PDF</Button>
              </Tooltip>
            )}
          </Cluster>
        </Stack>
      )}
    </Stack>
  );
}
